import React from 'react'

const RecipeStructuredData = ({ recipe }) => {
  if (!recipe) return null

  // Parse ingredients if they're stored as a string
  let ingredients = []
  try {
    ingredients = JSON.parse(recipe.ingredients || '[]')
  } catch (e) {
    ingredients = (recipe.ingredients || '').split(',').map((i) => i.trim())
  }

  // Parse instructions if they're stored as a string
  let instructions = []
  if (Array.isArray(recipe.instructions)) {
    instructions = recipe.instructions
  } else {
    try {
      instructions = JSON.parse(recipe.instructions || '[]')
    } catch (e) {
      instructions = (recipe.instructions || '').split(',').map((i) => i.trim())
    }
  }

  // Get the primary image URL from recipe_images if available, otherwise fallback to image_url
  const getPrimaryImageUrl = () => {
    if (recipe.recipe_images && recipe.recipe_images.length > 0) {
      // Sort by display_order to ensure we get the primary image, same logic as other components
      const sortedImages = [...recipe.recipe_images].sort((a, b) => {
        // Primary sort by display_order (ascending - 1-based, so 1, 2, 3...)
        if (a.display_order !== b.display_order) {
          return (a.display_order || 999) - (b.display_order || 999)
        }
        // Secondary sort by created_at (newest first) if display_order is the same
        return new Date(b.created_at || 0) - new Date(a.created_at || 0)
      })
      return sortedImages[0].image_url
    }
    return (
      recipe.image_url ||
      'https://forkast.s3.us-east-1.amazonaws.com/recipe-images/default-recipe-image.jpg'
    )
  }

  // Format ingredients for structured data
  const formattedIngredients = ingredients.map((ingredient) => {
    if (typeof ingredient === 'string') return ingredient
    const amount = ingredient.amount || ''
    const unit = ingredient.unit || ''
    const item = ingredient.item || ''
    return `${amount}${unit ? (unit === 'g' || unit === 'kg' || unit === 'mL' || unit === 'L' ? unit : ` ${unit}`) : ''} ${item}`.trim()
  })

  // Create the structured data object
  const structuredData = {
    '@context': 'https://schema.org/',
    '@type': 'Recipe',
    name: recipe.name,
    description: `Recipe for ${recipe.name}`,
    recipeIngredient: formattedIngredients,
    recipeInstructions: instructions.map((instruction) => ({
      '@type': 'HowToStep',
      text: instruction,
    })),
  }

  // Add optional fields if they exist
  if (recipe.cooking_time_minutes) {
    structuredData.cookTime = `PT${recipe.cooking_time_minutes}M`
    structuredData.totalTime = `PT${recipe.cooking_time_minutes}M`
  }

  const primaryImageUrl = getPrimaryImageUrl()
  if (primaryImageUrl) {
    structuredData.image = primaryImageUrl
  }

  if (recipe.users?.first_name && recipe.users?.last_name) {
    structuredData.author = {
      '@type': 'Person',
      name: `${recipe.users.first_name} ${recipe.users.last_name}`,
    }
  }

  return (
    <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
  )
}

export default RecipeStructuredData
